import { Link } from 'react-router-dom';
import LandingNav from '../components/LandingNav';
import Footer from '../components/Footer';
import PageSEO from '../components/PageSEO';
import { OCCASIONS } from '../data/occasions';

const BASE_URL = 'https://momentsent.com';

/**
 * Occasions hub: /occasions
 *
 * A substantive content hub (not a thin link list): every occasion carries a
 * unique blurb so the page reads as genuine content. Occasions that have a
 * full landing page link through to it; the rest still appear so the hub is
 * complete and gives crawlers clean internal links. Emits ItemList +
 * BreadcrumbList JSON-LD.
 */
export default function OccasionHubPage() {
  const canonical = `${BASE_URL}/occasions`;

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Occasions MomentSent mails cards for',
      itemListElement: OCCASIONS.map((o, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: `${o.occasion} cards`,
        ...(o.page ? { url: `${BASE_URL}${o.page.path}` } : {}),
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
        { '@type': 'ListItem', position: 2, name: 'Occasions', item: canonical },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <PageSEO
        title="Occasions We Mail Cards For | MomentSent"
        description="From birthdays and anniversaries to Christmas, Mother's Day, and just because, MomentSent mails a real, physical card on time for every occasion that matters. Set the date once and never forget again."
        canonical={canonical}
        structuredData={structuredData}
      />
      <LandingNav />

      {/* ── Hero ────────────────────────────────────────────────────────────── */}
      <section className="pt-28 pb-12 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-body text-sm text-sage font-semibold tracking-widest uppercase mb-4">
            Occasions
          </p>
          <h1 className="font-heading text-4xl md:text-5xl text-navy leading-tight mb-6">
            Every occasion that matters, never forgotten
          </h1>
          <p className="font-body text-lg text-navy/80 leading-relaxed">
            MomentSent mails real, physical cards for the moments that matter most. Add the dates
            once and we remember them for good, printing and mailing a genuine card with postage
            included so it arrives on time, every year. Below are the occasions people use
            MomentSent for most. Have something else in mind? You can schedule a card for any date
            and any reason.
          </p>
          <div className="mt-8">
            <Link to="/signup" className="btn-primary inline-block px-8 py-3 text-base">
              Start your free trial →
            </Link>
            <p className="mt-3 font-body text-xs text-gray-400">
              14-day free trial. No credit card required.
            </p>
          </div>
        </div>
      </section>

      {/* ── Occasion cards ──────────────────────────────────────────────────── */}
      <section className="pb-12 px-6">
        <div className="max-w-3xl mx-auto grid sm:grid-cols-2 gap-5">
          {OCCASIONS.map((o) => {
            const inner = (
              <>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl" aria-hidden="true">{o.emoji}</span>
                  <h2 className="font-heading text-xl text-navy group-hover:text-sage transition-colors">
                    {o.occasion}
                  </h2>
                </div>
                <p className="font-body text-sm text-gray-500 leading-relaxed">
                  {o.hubBlurb}
                </p>
                {o.page && (
                  <span className="mt-4 inline-block font-body text-sm text-sage font-semibold">
                    Learn more →
                  </span>
                )}
              </>
            );

            return o.page ? (
              <Link
                key={o.slug}
                to={o.page.path}
                className="group bg-white rounded-card p-6 shadow-warm border border-gray-100
                           hover:shadow-warm-lg hover:border-sage/40 transition-all"
              >
                {inner}
              </Link>
            ) : (
              <div
                key={o.slug}
                className="group bg-white rounded-card p-6 shadow-warm border border-gray-100"
              >
                {inner}
              </div>
            );
          })}
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────────────── */}
      <section className="py-20 px-6 bg-sage/10 mt-auto">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="font-heading text-3xl text-navy mb-4">
            Ready to never miss a moment?
          </h2>
          <p className="font-body text-base text-gray-500 leading-relaxed mb-8">
            Set the dates once and MomentSent mails a real card automatically, every year, to the
            people you love.
          </p>
          <Link to="/signup" className="btn-primary inline-block px-8 py-3 text-base">
            Start your free trial →
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
